/**
 * DesktopReceiptModal — internal, authenticated receipt view for the desktop app
 * (online AND offline). Replaces the fragile window.open popup, which in the exe
 * booted a second SPA with no backend URL and wiped the shared session.
 *
 * Renders the shared PodReceiptCard in the main window (correct backend + auth):
 *   - online: fetches media through the established authenticated endpoints;
 *   - offline: fetches available protected media through those same business
 *              routes and keeps unavailable items as contained placeholders.
 * Portaled to <body> so the print rule (index.css) can hide #root and print only
 * the receipt. Deliberately NOT the public /pod/:id route — no anonymous surface.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Printer } from 'lucide-react';

import { API } from '@/config/constants';
import { isDesktopOffline } from '@/api/desktopSync';
import PodReceiptCard from '@/components/delivery/PodReceiptCard';
import { useAuthenticatedMedia } from '@/hooks/useAuthenticatedMedia';
import { adaptOrderToReceipt } from '@/utils/offlineReceipt';

const btnClass =
  'flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-xl text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors shadow-sm';

export default function DesktopReceiptModal({ order, onClose }) {
  const offline = isDesktopOffline();
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const mediaRequests = useMemo(() => {
    const files = order?.delivery_confirmation?.media_files || [];
    const requests = files
      .map((medium, index) => (
        medium?.storage_path
          ? {
            key: `delivery:${index}`,
            url: `${API}/delivery-media/${encodeURIComponent(medium.storage_path)}`,
          }
          : null
      ))
      .filter(Boolean);
    const arrangementPath = order?.arrangement_photo?.storage_path;
    if (arrangementPath) {
      requests.push({
        key: 'arrangement',
        url: `${API}/arrangement-photo/${encodeURIComponent(arrangementPath)}`,
      });
    }
    return requests;
  }, [order]);
  const loadedMedia = useAuthenticatedMedia(mediaRequests);
  const mediaUrls = Object.fromEntries(
    (order?.delivery_confirmation?.media_files || []).map((_, index) => [
      index,
      loadedMedia[`delivery:${index}`]?.url,
    ]),
  );
  const mediaStates = Object.fromEntries(
    (order?.delivery_confirmation?.media_files || []).map((_, index) => [
      index,
      loadedMedia[`delivery:${index}`],
    ]),
  );
  const arrangementUrl = loadedMedia.arrangement?.url || null;

  useEffect(() => {
    document.body.classList.add('receipt-overlay-open');
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('receipt-overlay-open');
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const data = adaptOrderToReceipt(order);

  return createPortal(
    <div
      className="receipt-overlay fixed inset-0 z-[90] overflow-y-auto bg-stone-100 print:static print:overflow-visible print:bg-white"
      data-testid="desktop-receipt-overlay"
    >
      <div className="max-w-2xl mx-auto py-6 px-4 print:py-0 print:px-0">
        <div className="flex justify-between mb-3 print:hidden">
          <button onClick={onClose} className={btnClass} data-testid="desktop-receipt-close">
            <X className="h-4 w-4" />
            Close
          </button>
          <button onClick={() => window.print()} className={btnClass} data-testid="desktop-receipt-print">
            <Printer className="h-4 w-4" />
            Print
          </button>
        </div>
        <PodReceiptCard
          data={data}
          orderId={order.id}
          offline={offline}
          mediaUrls={mediaUrls}
          mediaStates={mediaStates}
          arrangementUrl={arrangementUrl}
          arrangementState={loadedMedia.arrangement}
          onImageClick={setLightboxSrc}
        />
      </div>

      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 print:hidden"
          onClick={() => setLightboxSrc(null)}
          data-testid="desktop-receipt-lightbox"
        >
          <img
            src={lightboxSrc}
            alt="Full size"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>,
    document.body,
  );
}
